import { User } from "@supabase/supabase-js";
import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "./supabase";

type AuthContextType = {
  user: User | null;
  loading: boolean;
  showOrganizationPopup: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    fullName: string,
    organizationName?: string,
  ) => Promise<void>;
  signOut: () => Promise<void>;
  createOrganization: (organizationName: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showOrganizationPopup, setShowOrganizationPopup] = useState(false);

  // Check if user has an organization
  const checkUserOrganization = async (accessToken: string) => {
    try {
      // Get user data from database to check for organization_id
      let hasOrganization = false;

      if (user?.id) {
        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("organization_id")
          .eq("id", user.id)
          .single();

        // If user has organization_id in the database, they have an organization
        if (!userError && userData?.organization_id) {
          console.log(
            "User has organization from database check:",
            userData.organization_id,
          );
          setShowOrganizationPopup(false);
          return true;
        }
      }

      return true;
    } catch (error) {
      console.error("Failed to check user organization:", error);
      return false;
    }
  };

  // Create a new organization for the user
  const createOrganization = async (organizationName: string) => {
    if (!user || !user.id) {
      throw new Error("User not authenticated");
    }

    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session?.access_token) {
        throw new Error("No active session");
      }

      // Add timeout protection to the fetch
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      try {
        const response = await fetch(
          `${
            import.meta.env.VITE_SUPABASE_URL
          }/functions/v1/create_organization`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.data.session.access_token}`,
            },
            body: JSON.stringify({ name: organizationName }),
            signal: controller.signal,
          },
        );

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json();
          console.error("Error creating organization:", errorData);
          throw new Error(errorData?.error || "Failed to create organization");
        }

        // Organization created successfully, hide the popup
        setShowOrganizationPopup(false);
      } catch (error: any) {
        clearTimeout(timeoutId);

        if (error.name === "AbortError") {
          throw new Error("Organization creation timed out. Please try again.");
        }

        throw error;
      }
    } catch (error) {
      console.error("Failed to create organization:", error);
      throw error;
    }
  };

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);

      // Check if user has an organization when session is loaded
      if (session?.access_token && session?.user) {
        // await checkUserOrganization(session.access_token);
      }
    });

    // Listen for changes on auth state (signed in, signed out, etc.)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Tab refocus (SIGNED_IN) and TOKEN_REFRESHED hand back a new object for
      // the same user; keep the old one so every [user] effect doesn't refetch.
      const next = session?.user ?? null;
      setUser((prev) =>
        prev && next && prev.id === next.id && event !== "USER_UPDATED" ? prev : next
      );
      setLoading(false);

      // Check if user has an organization when auth state changes
      if (session?.access_token && session?.user) {
        await checkUserOrganization(session.access_token);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    organizationName?: string,
  ) => {
    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });
    if (error) throw error;

    // If user is created and has a session, create organization if name provided
    if (data.session?.access_token && organizationName) {
      try {
        await createOrganization(organizationName);
      } catch (orgError) {
        console.error("Failed to create organization during signup:", orgError);
        // Continue with signup even if org creation fails
      }
    } else if (data.session?.access_token) {
      // If no organization name provided, check if they have an organization
      await checkUserOrganization(data.session.access_token);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error, data } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;

    // After sign in, check if user has an organization
    if (data.session?.access_token) {
      await checkUserOrganization(data.session.access_token);
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        showOrganizationPopup,
        signIn,
        signUp,
        signOut,
        createOrganization,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
